import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Flame, Trophy } from "lucide-react";
import { historyMonthQuery } from "@/lib/history-queries";
import {
  monthFromSlug,
  monthSlug,
  monthYearLabel,
  stepMonth,
  type HistorySnapshot,
} from "@/lib/history";
import { BOARDS_OG_IMAGE, BOARDS_OG_IMAGE_ALT, SITE_URL, buildPageMetadata } from "@/lib/seo";
import { translate, useT } from "@/i18n";
import { detectLocale } from "@/i18n/runtime";
import { formatHeat } from "@/lib/behot";

export const Route = createFileRoute("/history/$year/$month/")({
  loader: async ({ context, params }) => {
    const year = Number(params.year);
    const month = monthFromSlug(params.month);
    if (!/^\d{4}$/.test(params.year) || !month) throw notFound();
    const snapshots = await context.queryClient.ensureQueryData(historyMonthQuery(year, month));
    return { year, month, count: snapshots.length };
  },
  head: ({ params, loaderData }) => {
    const locale = detectLocale();
    const month = monthFromSlug(params.month);
    const year = Number(params.year);
    if (!loaderData || !month) {
      return buildPageMetadata({
        path: `/history/${params.year}/${params.month}`,
        title: "Unavailable | BeHot.Now",
        description: "This monthly ranking archive is not available.",
        noindex: true,
      });
    }
    const monthYear = monthYearLabel(month, year, locale);
    const path = `/history/${year}/${params.month}`;
    return buildPageMetadata({
      path,
      title: translate(locale, "history.month.title", { monthYear }),
      description: translate(locale, "history.month.description", { monthYear }),
      ogTitle: translate(locale, "history.month.h1", { monthYear }),
      ogDescription: translate(locale, "history.month.description", { monthYear }),
      image: BOARDS_OG_IMAGE,
      imageAlt: BOARDS_OG_IMAGE_ALT,
      keywords: [`BeHot.Now rankings ${monthYear}`, `top rankings ${monthYear}`],
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: translate(locale, "history.nav"), path: "/history" },
        { name: params.year, path: `/history/${year}` },
        { name: monthYear, path },
      ],
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: translate(locale, "history.month.h1", { monthYear }),
        url: `${SITE_URL}${path}`,
        description: translate(locale, "history.month.description", { monthYear }),
        isPartOf: { "@type": "WebSite", url: SITE_URL, name: "BeHot.Now" },
      },
    });
  },
  component: HistoryMonthPage,
});

function BoardCard({ s, monthSlugValue }: { s: HistorySnapshot; monthSlugValue: string }) {
  const { t } = useT();
  return (
    <li>
      <Link
        to="/history/$year/$month/$board"
        params={{ year: String(s.year), month: monthSlugValue, board: s.category_slug }}
        search={{ page: undefined }}
        className="card-surface block p-4 transition-colors hover:border-primary/50"
      >
        <h3 className="font-display text-lg font-bold">{s.category_name}</h3>
        {s.category_description && (
          <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
            {s.category_description}
          </p>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span>{t("history.entries", { n: s.entry_count })}</span>
          <span className="inline-flex items-center gap-1 font-semibold text-primary">
            <Flame className="size-3.5" />
            {formatHeat(s.total_heat_cents)}
          </span>
        </div>
        <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary">
          {t("history.viewRanking")}
          <ArrowRight className="size-3.5" />
        </span>
      </Link>
    </li>
  );
}

function HistoryMonthPage() {
  const { year, month } = Route.useLoaderData();
  const { t, locale } = useT();
  const { data: snapshots } = useSuspenseQuery(historyMonthQuery(year, month));
  const monthYear = monthYearLabel(month, year, locale);
  const slug = monthSlug(month);
  const prev = stepMonth(year, month, -1);
  const next = stepMonth(year, month, 1);
  const entries = snapshots.reduce((sum, s) => sum + s.entry_count, 0);

  return (
    <div className="mx-auto max-w-5xl px-4 py-10">
      <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          BeHot.Now
        </Link>
        <span className="px-1.5">/</span>
        <Link to="/history" className="hover:text-foreground">
          {t("history.nav")}
        </Link>
        <span className="px-1.5">/</span>
        <Link to="/history/$year" params={{ year: String(year) }} className="hover:text-foreground">
          {year}
        </Link>
        <span className="px-1.5">/</span>
        <span className="capitalize text-foreground">{monthYear}</span>
      </nav>

      <header className="mt-4">
        <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
          <Trophy className="size-3.5" />
          {t("history.pastRankings")}
        </span>
        <h1 className="mt-3 font-display text-3xl font-black sm:text-4xl">
          {t("history.month.h1", { monthYear })}
        </h1>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          {t("history.month.intro", { monthYear, boards: snapshots.length, entries })}
        </p>
      </header>

      <h2 className="mt-10 font-display text-xl font-bold">
        {t("history.month.boards", { monthYear })}
      </h2>
      <ul className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {snapshots.map((s) => (
          <BoardCard key={s.id} s={s} monthSlugValue={slug} />
        ))}
      </ul>

      <div className="mt-10 flex flex-wrap items-center justify-between gap-3 text-sm">
        <Link
          to="/history/$year/$month"
          params={{ year: String(prev.year), month: monthSlug(prev.month) }}
          className="inline-flex items-center gap-1 text-primary hover:underline"
        >
          <ArrowLeft className="size-4" />
          {t("history.board.previousMonth")}
        </Link>
        <Link
          to="/history/$year/$month"
          params={{ year: String(next.year), month: monthSlug(next.month) }}
          className="inline-flex items-center gap-1 text-primary hover:underline"
        >
          {t("history.board.nextMonth")}
          <ArrowRight className="size-4" />
        </Link>
      </div>
    </div>
  );
}
